'use client';

import { useCallback, useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import { HomeRes } from '@/types/api/hall/home.d';
import DotButton from '../../components/dot-button';
import styles from './index.module.css';
import { ThirdPartyAuthButtonsDisplay } from '@/features/auth/components/third-party-auth/third-party-auth-buttons-display';
import { showLoginModal } from '@/features/auth/components/login-form';
import useFn from '@/hooks/use-fn';
import { track } from '@/utils/track';
import type { CardBannerItem } from '../../services/home';
import { Button } from '@/components/ui/button';
import { useTranslations } from 'next-intl';
import { BannerAutoPlayProps } from '../../const/game';
import Autoplay from 'embla-carousel-autoplay';
import { LOGIN_SOURCE_MAP } from '@/const/report';
import { useRouter, Link } from '@/i18n/navigation';
import { jumpByUrl, isJumpAction, parseJumpAction } from '@/utils/jump-url';
import { JUMP_ACTION } from '@/const/jump-action';
import { useThrottleByRef } from '../../hooks/use-throttle-expose';
import { useInViewport } from 'ahooks';
import { useModalOverlay } from '../../hooks/use-modal-overlay';
interface UnloginBannerProps {
  bannerImages: HomeRes['top_banner'];
}

export default function UnloginBanner({ bannerImages = [] }: UnloginBannerProps) {
  const t = useTranslations('auth');
  const router = useRouter();
  const handleShowLogin = useFn(() => {
    showLoginModal();
    track('register_login_entrance_click', {
      current_module: LOGIN_SOURCE_MAP.Home,
    });
  });

  const [currentSlide, setCurrentSlide] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: true,
    duration: 0, // 禁用过渡动画
    dragFree: false,
  }, [
    Autoplay(BannerAutoPlayProps)
  ]);
  const postTrackRef = useRef<boolean | undefined>(undefined);
  const bannerWrapperRef = useRef<HTMLDivElement | null>(null);
  const [isBannerInViewport] = useInViewport(bannerWrapperRef);
  const hasModalOverlay = useModalOverlay();

  const handleBannerShow = (item: CardBannerItem, index: number) => {
    if (!postTrackRef.current) return;
    track('banner_exposure', {
      current_page: 'Home',
      current_module: item?.module_name,
      banner_number: index + 1,
      to_url: item?.link || '',
    });
  };
  // 调用 Hook，传入上报函数和等待时间
  const bannerShow = useThrottleByRef({
    wait: 20 * 1000,
  });

  const goToSlide = useCallback(
    (index: number) => {
      if (emblaApi) {
        emblaApi.scrollTo(index, true); // 第二个参数为 true 表示立即跳转，无动画
      }
    },
    [emblaApi]
  );

  // 预加载所有内部链接的 banner 页面
  useEffect(() => {
    if (bannerImages && bannerImages.length > 0) {
      bannerImages.forEach((item) => {
        const { link, has_activity_common_detail } = item || {};
        // 只预加载内部链接
        if (link && !/^https?:\/\//i.test(link)) {
          router.prefetch(link);
        } else if (!link && has_activity_common_detail) {
          router.prefetch(`/banner-detail?id=${item.uuid}&type=1`);
        }
      });
    }
  }, [bannerImages, router]);

  // 监听slide变化
  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      const newSlide = emblaApi.selectedScrollSnap();
      setCurrentSlide((prev) => {
        if (bannerImages?.[prev]?.uuid) {
          bannerShow(bannerImages?.[prev]?.uuid, () => {
            handleBannerShow(bannerImages?.[prev] as CardBannerItem, prev);
          });
        }
        return newSlide;
      });
    };

    emblaApi.on('select', onSelect);
    onSelect(); // 初始化时设置当前slide

    // 如果只有一张图，初始化时直接上报
    if (bannerImages?.length === 1 && bannerImages[0]?.uuid) {
      bannerShow(bannerImages[0].uuid, () => {
        handleBannerShow(bannerImages[0] as CardBannerItem, 0);
      });
    }

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, bannerImages, bannerShow]);

  const handleClick = (item: CardBannerItem, index: number) => {
    const { link, has_activity_common_detail } = item || {};
    track('banner_click', {
      current_page: 'Home',
      current_module: item?.module_name,
      banner_number: index + 1,
      to_url: link,
    });
    if (link) {
      if (isJumpAction(link)) {
        const { action } = parseJumpAction(link) || {};
        const walletActions = [
          JUMP_ACTION.OPEN_DEPOSIT_CASH,
          JUMP_ACTION.OPEN_DEPOSIT_CRYPTO,
          JUMP_ACTION.OPEN_WITHDRAW_CASH,
          JUMP_ACTION.OPEN_WITHDRAW_CRYPTO,
        ];
        if (action && walletActions.includes(action)) {
          showLoginModal();
          return;
        }
      }
      jumpByUrl(link, router, item?.module_name);
    } else if (has_activity_common_detail) {
      router.push(`/banner-detail?id=${item.uuid}&type=1`);
    }
  };

  useEffect(() => {
    // 只有当 banner 在视口中且没有弹窗遮挡时才允许上报
    postTrackRef.current = !!isBannerInViewport && !hasModalOverlay;
  }, [isBannerInViewport, hasModalOverlay]);

  return (
    <div
      ref={bannerWrapperRef}
      className={clsx(
        'relative w-full mx-auto',
        'flex flex-col items-center justify-center',
        'overflow-hidden',
      )}
    >
      {/* 指示器 */}
      {emblaApi && (
        <div className="absolute top-[15px] left-[12px] z-10">
          <DotButton
            totalSlides={bannerImages?.length || 0}
            currentSlide={currentSlide}
            onSlideChange={goToSlide}
          />
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center w-full relative flex-1">
        {/* 这里放之前的轮播banner */}
        {/* 轮播图容器 */}
        <div className={clsx(styles.embla, 'w-full min-h-[260px] relative overflow-hidden')}>
          <div className={styles.emblaViewport} ref={emblaRef}>
            <div className={styles.emblaContainer}>
              {bannerImages?.map((obj, index) => {
                const { link, has_activity_common_detail } = obj || {};
                const isExternalLink = link && /^https?:\/\//i.test(link);
                const internalLink = !isExternalLink && link ? link : has_activity_common_detail ? `/banner-detail?id=${obj.uuid}&type=1` : '';

                const imageElement = obj?.img ? (
                  <div className="flex items-center justify-center relative w-full">
                    <Image
                      src={obj?.img || ''}
                      alt={`Banner ${index + 1}`}
                      className="object-contain w-full"
                      priority={index === 0}
                      width={300}
                      height={100}
                      onClick={isExternalLink ? () => handleClick(obj as CardBannerItem, index) : undefined}
                    />
                  </div>
                ) : (
                  <div className={clsx('w-full h-[175px] bg-gray-900')} />
                );

                return (
                  <div key={index} className={styles.emblaSlide}>
                    {isExternalLink ? (
                      imageElement
                    ) : (
                      <Link
                        href={internalLink}
                        prefetch={true}
                        className="block w-full h-full"
                        onClick={(e) => {
                          e.preventDefault();
                          handleClick(obj as CardBannerItem, index);
                        }}
                      >
                        {imageElement}
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Login Button */}
        <Button
          variant="primary"
          className="w-[282px]"
          onClick={handleShowLogin}
        >
          {t('loginSignup')}
        </Button>

        {/* 三方登陆 */}
        <div className="flex gap-3 justify-center mt-[15px]" onClick={handleShowLogin}>
          <div className='pointer-events-none'>
            <ThirdPartyAuthButtonsDisplay className='w-[282px] justify-center' />
          </div>
        </div>
      </div>
    </div>
  );
}
